import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { withRouter, Switch as RouterSwitch } from 'react-router-dom'
import {
  Button,
  Card,
  Col,
  Drawer,
  Empty,
  Input,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Timeline,
  Upload
} from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import PageLoader from '../../components/Layouts/PageLoader'
import PrivateRoute from '../../hocs/PrivateRoute'
import NewPolicyModal from './NewPolicyModal'
import PolicyDetailsForm from './PolicyDetailsForm'
import PolicyDocuments from './PolicyDocuments'
import PolicyPayment from './PolicyPayment'
import PolicyPrices, { chooseInsurer } from './PolicyPrices'
import {
  getRequest,
  postRequest,
  insurancePoliciesUrl,
  insurancePolicyCardsUrl,
  insurancePolicyCountsUrl,
  insurancePolicyActionUrl,
  successNotification,
  errorNotification
} from '../../config'

const { Option } = Select

// The order a worklist is actually worked: a policy the customer has paid for and nobody has sent
// anywhere is the most urgent thing on this screen, and a delivered one is finished.
const STATUS_ORDER = [
  'paid',
  'submitted',
  'underwritten',
  'delivered',
  'awaiting_payment',
  'declined'
]

const STATUS_LABELS = {
  collecting: 'Collecting',
  quoted: 'Choosing an insurer',
  details: 'Collecting details',
  awaiting_payment: 'Awaiting payment',
  paid: 'Paid — to submit',
  submitted: 'With the insurer',
  underwritten: 'Underwritten',
  delivered: 'Delivered',
  declined: 'Declined',
  refunded: 'Refunded',
  cancelled: 'Cancelled'
}

const STATUS_COLOURS = {
  paid: 'volcano',
  submitted: 'gold',
  underwritten: 'blue',
  delivered: 'green',
  declined: 'red',
  refunded: 'purple',
  awaiting_payment: 'orange'
}

// Which door a policy came through. Worth showing, because it changes what you can assume: an
// app policy answered its own questions, and an agent policy was typed by one of us from what a
// customer read out over WhatsApp.
const CHANNEL_LABELS = {
  app: 'The mobile app',
  whatsapp: 'WhatsApp assistant',
  agent: 'Created by our team'
}

const DECLINE_DISPOSITIONS = [
  { value: 'refund_full', label: 'Refund everything' },
  { value: 'refund_premium', label: 'Refund the premium, keep the service fee' },
  { value: 'requote', label: 'Re-quote with another insurer' }
]

// `postRequest` reports failures as an axios error and fires its own generic toast. The sentence
// worth showing is the server's — it names which policy and what is wrong with it — so every
// action digs it out rather than settling for "Error creating submit, retry."
const serverSaid = response =>
  response.error?.response?.data?.detail || response.data?.detail

const money = (amount, currency) =>
  amount === null || amount === undefined ? '—' : `${currency || 'GHS'} ${amount}`

const PoliciesList = () => {
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState([])
  const [counts, setCounts] = useState({})
  const [statusFilter, setStatusFilter] = useState(undefined)
  const [open, setOpen] = useState(null) // the policy in the drawer
  const [action, setAction] = useState(null) // which modal is open
  const [form, setForm] = useState({})
  const [saving, setSaving] = useState(false)
  const [file, setFile] = useState(null)
  const [creating, setCreating] = useState(false)
  const [cards, setCards] = useState([])

  const load = async (status = statusFilter) => {
    const url = status ? `${insurancePoliciesUrl}/?status=${status}` : `${insurancePoliciesUrl}/`
    const response = await getRequest(url, 'policies', setLoading)
    if (response.success) setRows(response.data.results || response.data)
    const tally = await getRequest(insurancePolicyCountsUrl, 'counts', () => {})
    if (tally.success) setCounts(tally.data || {})
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line
  }, [])

  /*
    Open straight onto one policy, from a link.

    The Telegram alert that fires when a customer says they have paid names the policy; without
    this it could only point at the list, and whoever is on call would then have to find the row
    — at the exact moment speed is the point, because somebody has sent money and is waiting.

    A query parameter rather than a route of its own: the drawer is not a page, the list behind it
    still has to load, and `/insurance/policies` on its own must keep working. Fetched by id
    rather than searched for in `rows`, because the policy that needs attention is not always on
    the first page of a list sorted by status.
  */
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get('open')
    if (!id) return
    const openOne = async () => {
      const response = await getRequest(`${insurancePoliciesUrl}/${id}/`, 'policy', () => {})
      if (response.success) setOpen(response.data)
      else errorNotification('That policy could not be opened. It may have been removed.')
    }
    openOne()
    // eslint-disable-next-line
  }, [])

  const refresh = async id => {
    await load()
    if (id) {
      const response = await getRequest(
        `${insurancePoliciesUrl}/${id}/`,
        'policy',
        () => {}
      )
      if (response.success) setOpen(response.data)
    }
  }

  /*
    A policy quoted and left is a policy that has to be FINISHABLE.

    "Close — I'll finish this later" is offered on the create modal, so a customer who rings off
    halfway keeps their quote — and that promise is only worth anything if the prices can be
    reached again. Without this the drawer of a `quoted` policy was a dead end: everything visible,
    nothing choosable.
  */
  useEffect(() => {
    if (!open || open.insurerName) {
      setCards([])
      return
    }
    const load = async () => {
      const response = await getRequest(
        insurancePolicyCardsUrl(open.id),
        'prices',
        () => {}
      )
      if (response.success) setCards(response.data.cards || [])
    }
    load()
    // eslint-disable-next-line
  }, [open?.id, open?.insurerName])

  /*
    What the panels call when they have changed something.

    They are handed the server's OWN copy of the policy where they have one, which is the whole
    point: a panel that patched its local copy would show a state the server may not agree with.
    Passing null means "you changed something and did not get a policy back" — re-read it.

    The list is refreshed either way, because a status or a payment changes what the worklist says.
  */
  const afterChange = async policy => {
    if (policy) setOpen(policy)
    await refresh(policy ? null : open.id)
  }

  // Every action posts and then re-reads. The server owns the state machine — a client that
  // patched its own copy would show a transition the server may have refused.
  const runAction = async (name, payload) => {
    setSaving(true)
    const response = await postRequest(
      insurancePolicyActionUrl(open.id, name),
      payload || {},
      name,
      () => {}
    )
    setSaving(false)
    if (!response.success) {
      errorNotification(serverSaid(response) || 'That did not work.')
      return
    }
    successNotification('Done.')
    setAction(null)
    setForm({})
    await refresh(open.id)
  }

  const uploadCertificate = async () => {
    if (!file) return
    setSaving(true)
    const data = new FormData()
    data.append('file', file)
    const response = await postRequest(
      insurancePolicyActionUrl(open.id, 'certificate'),
      data,
      'certificate',
      () => {},
      false // multipart
    )
    setSaving(false)
    if (!response.success) {
      errorNotification(serverSaid(response) || 'That file could not be stored.')
      return
    }
    successNotification('Certificate attached.')
    setAction(null)
    setFile(null)
    await refresh(open.id)
  }

  const deliver = async force => {
    setSaving(true)
    const response = await postRequest(
      insurancePolicyActionUrl(open.id, 'deliver'),
      { force: !!force },
      'deliver',
      () => {}
    )
    setSaving(false)
    if (!response.success) {
      errorNotification(serverSaid(response) || 'Delivery could not be attempted.')
      return
    }
    // Per channel, because they fail independently: a customer who can see their certificate in
    // the app is not blocked by Meta refusing us, and the screen should say exactly that.
    const results = response.data.results || {}
    Object.entries(results).forEach(([channel, state]) => {
      const line = `${channel}: ${state}`
      if (state === 'failed') errorNotification(line)
      else successNotification(line)
    })
    await refresh(open.id)
  }

  /*
   * antd `render` functions return JSX, which the display-name rule reads as an anonymous
   * component. They are cell renderers — disabled the same way the MSRP whitelist and the
   * car-request table already do.
   */
  /* eslint-disable react/display-name */
  const columns = [
    {
      title: 'Policy',
      key: 'label',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <strong>{r.registrationNumber || r.make || 'Quote'}</strong>
          <span style={{ opacity: 0.65 }}>
            {[r.make, r.model].filter(Boolean).join(' ') || r.vehicleClassName}
          </span>
        </Space>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (_, r) => (
        <Tag color={STATUS_COLOURS[r.status]}>{STATUS_LABELS[r.status] || r.status}</Tag>
      )
    },
    {
      title: 'Cover',
      dataIndex: 'policyTypeName',
      render: (_, r) => r.policyTypeName || '—'
    },
    { title: 'Insurer', dataIndex: 'insurerName', render: (_, r) => r.insurerName || '—' },
    {
      title: 'Customer pays',
      key: 'total',
      render: (_, r) => money(r.totalAmount, r.currency)
    },
    {
      title: 'Waiting',
      dataIndex: 'waitingHours',
      // `late` is the SERVER's answer, in working hours. This screen used to carry its own copy of
      // the thresholds, which disagreed with the Telegram alert the moment one was changed in a
      // .env — and counted the weekends the alert deliberately does not.
      render: (_, r) =>
        r.waitingHours === null || r.waitingHours === undefined ? (
          '—'
        ) : (
          <span style={{ color: r.late ? '#cf1322' : undefined }}>{r.waitingHours}h</span>
        )
    },
    {
      title: '',
      key: 'open',
      render: (_, r) => (
        <Button size="small" onClick={() => setOpen(r)}>
          Open
        </Button>
      )
    }
  ]

  /* eslint-enable react/display-name */

  return (
    <PageLoader loading={loading}>
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {STATUS_ORDER.filter(s => counts[s]).map(status => (
          <Col key={status} xs={12} md={4}>
            <Card
              size="small"
              hoverable
              onClick={() => {
                setStatusFilter(status)
                load(status)
              }}
            >
              <div style={{ opacity: 0.65, fontSize: 12 }}>{STATUS_LABELS[status]}</div>
              <div style={{ fontSize: 22, fontWeight: 600 }}>{counts[status]}</div>
            </Card>
          </Col>
        ))}
      </Row>

      <Space style={{ marginBottom: 12 }}>
        <Select
          allowClear
          placeholder="All statuses"
          style={{ width: 220 }}
          value={statusFilter}
          onChange={value => {
            setStatusFilter(value)
            load(value)
          }}
        >
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <Option key={value} value={value}>
              {label}
            </Option>
          ))}
        </Select>
        <Button onClick={() => load()}>Refresh</Button>
        {/*
          The front door. Primary, because while the app is unpublished this is how policies are
          actually sold — a person on WhatsApp, quoting and collecting by hand.
        */}
        <Button type="primary" onClick={() => setCreating(true)}>
          New policy
        </Button>
      </Space>

      <Table
        rowKey="id"
        dataSource={rows}
        columns={columns}
        pagination={{ pageSize: 25 }}
        locale={{ emptyText: <Empty description="No policies yet" /> }}
      />

      <Drawer
        width={720}
        visible={!!open}
        onClose={() => setOpen(null)}
        title={open ? open.registrationNumber || 'Policy' : ''}
      >
        {open && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card size="small" title="What the customer answered">
              <Row gutter={[8, 8]}>
                <Detail label="Cover" value={open.policyTypeName} />
                <Detail label="Vehicle class" value={open.vehicleClassName} />
                <Detail label="Seats" value={open.seats} />
                <Detail label="Vehicle value" value={open.vehicleValue} />
                <Detail label="Phone" value={open.phone} />
                <Detail label="Expires" value={open.expiryDate} />
                <Detail label="Came from" value={CHANNEL_LABELS[open.channel] || open.channel} />
              </Row>
            </Card>

            {/*
              The prices, for a policy nobody has chosen an insurer for yet.

              Reached from here as well as from the create modal, because a quote left overnight is
              the normal case, not the exceptional one — and a drawer that showed the car but not
              the prices was a screen the sale could fall between.
            */}
            {!open.insurerName ? (
              <Card size="small" title="Prices">
                <PolicyPrices
                  cards={cards}
                  onChoose={card => chooseInsurer(open.id, card, afterChange)}
                />
              </Card>
            ) : null}

            {/*
              The car, as one editable form rather than a read-only grid.

              It used to be a list of values with no way to change them, which was right while
              every policy came from the app and arrived complete. An agent creating one is
              filling these in as the customer reads their logbook out, so the same panel has to
              take input — and it collapses back to a summary once it is done.
            */}
            <PolicyDetailsForm policy={open} onChanged={afterChange} />

            <PolicyPayment policy={open} onChanged={afterChange} />

            <PolicyDocuments policy={open} onChanged={afterChange} />

            {/*
              The only screen where the split exists. The customer paid ONE figure and every
              customer-facing payload sends only that; premium, fee and commission live here and in
              the invoice.
            */}
            <Card size="small" title="The money">
              <Row gutter={[8, 8]}>
                <Detail label="Premium (owed onward)" value={money(open.premiumAmount, open.currency)} />
                <Detail label="Our service fee" value={money(open.serviceFeeAmount, open.currency)} />
                <Detail label="Customer paid" value={money(open.totalAmount, open.currency)} />
                <Detail
                  label="Our commission"
                  value={`${money(open.commissionAmount, open.currency)}${
                    open.commissionKind === 'percent' ? ` (${open.commissionValue}%)` : ''
                  }`}
                />
                <Detail label="Reference" value={open.transactionReference} />
                <Detail label="Paid" value={open.isPaid ? 'Yes' : 'No'} />
              </Row>
            </Card>

            <Card size="small" title="Fulfilment">
              <Row gutter={[8, 8]}>
                <Detail label="Policy number" value={open.policyNumber} />
                <Detail label="Insurer reference" value={open.insurerReference} />
                <Detail label="Submitted" value={open.submittedAt} />
                <Detail label="Underwritten" value={open.underwrittenAt} />
                <Detail label="Delivered" value={open.deliveredAt} />
                <Detail
                  label="Certificate"
                  value={
                    open.certificateUrl ? (
                      <a href={open.certificateUrl} target="_blank" rel="noopener noreferrer">
                        Open
                      </a>
                    ) : (
                      'Not attached'
                    )
                  }
                />
              </Row>
              <Space wrap style={{ marginTop: 12 }}>
                <Button
                  type="primary"
                  disabled={open.status !== 'paid'}
                  onClick={() => setAction('submit')}
                >
                  Mark submitted
                </Button>
                <Button
                  disabled={open.status !== 'submitted'}
                  onClick={() => setAction('underwrite')}
                >
                  Record underwriting
                </Button>
                <Button
                  disabled={!['underwritten', 'delivered'].includes(open.status)}
                  onClick={() => setAction('certificate')}
                >
                  Attach certificate
                </Button>
                <Button
                  disabled={!open.certificateUrl}
                  onClick={() => deliver(open.status === 'delivered')}
                  loading={saving}
                >
                  {open.status === 'delivered' ? 'Send again' : 'Deliver'}
                </Button>
                <Button
                  danger
                  disabled={!['paid', 'submitted', 'underwritten'].includes(open.status)}
                  onClick={() => setAction('decline')}
                >
                  Declined by insurer
                </Button>
              </Space>
            </Card>

            {/*
              What happened, and who did it.

              Someone else's money, through our hands, on someone else's promise — so "when did we
              submit this" must have an answer that is not somebody's memory. The transitions have
              always been recorded; this is the first screen that shows them.
            */}
            {open.events?.length ? (
              <Card size="small" title="History">
                <Timeline>
                  {open.events.map((event, index) => (
                    <Timeline.Item
                      key={`${event.createdAt}-${index}`}
                      color={event.toStatus === 'declined' ? 'red' : 'blue'}
                    >
                      <Space direction="vertical" size={0}>
                        <span>
                          {event.fromStatus !== event.toStatus ? (
                            <strong>
                              {STATUS_LABELS[event.toStatus] || event.toStatus}
                            </strong>
                          ) : null}
                          {event.note ? ` ${event.note}` : ''}
                        </span>
                        <span style={{ opacity: 0.6, fontSize: 12 }}>
                          {new Date(event.createdAt).toLocaleString()}
                          {event.actor ? ` · ${event.actor}` : ''}
                        </span>
                      </Space>
                    </Timeline.Item>
                  ))}
                </Timeline>
              </Card>
            ) : null}
          </Space>
        )}
      </Drawer>

      {/*
        Creating one. Opens the new policy's drawer the moment an insurer is chosen, so the agent
        goes straight on to the details while the customer is still on the line.
      */}
      <NewPolicyModal
        visible={creating}
        onClose={() => setCreating(false)}
        onCreated={async policy => {
          setCreating(false)
          setOpen(policy)
          await load()
        }}
      />

      <Modal
        visible={action === 'submit'}
        title="Mark submitted to the broker"
        okText="Submitted"
        confirmLoading={saving}
        onCancel={() => setAction(null)}
        onOk={() => runAction('submit', { note: form.note || '' })}
      >
        <p>Records that this application has gone to the broker, and starts its clock.</p>
        <Input.TextArea
          rows={2}
          placeholder="Note (optional) — how it was sent"
          value={form.note}
          onChange={e => setForm({ ...form, note: e.target.value })}
        />
      </Modal>

      <Modal
        visible={action === 'underwrite'}
        title="Record underwriting"
        okText="Record"
        confirmLoading={saving}
        onCancel={() => setAction(null)}
        onOk={() => runAction('underwrite', form)}
      >
        <label>Policy number</label>
        <Input
          value={form.policy_number}
          onChange={e => setForm({ ...form, policy_number: e.target.value })}
        />
        <p style={{ opacity: 0.65, marginTop: 4 }}>
          Required. It is what the broker, the insurer and the commission invoice all join on.
        </p>
        <label>Insurer reference (optional)</label>
        <Input
          value={form.insurer_reference}
          onChange={e => setForm({ ...form, insurer_reference: e.target.value })}
        />
      </Modal>

      <Modal
        visible={action === 'certificate'}
        title="Attach the certificate"
        okText="Upload"
        confirmLoading={saving}
        onCancel={() => {
          setAction(null)
          setFile(null)
        }}
        onOk={uploadCertificate}
        okButtonProps={{ disabled: !file }}
      >
        <p>The document the insurer issued. PDF or image, up to 10MB.</p>
        <Upload
          beforeUpload={selected => {
            setFile(selected)
            return false
          }}
          maxCount={1}
          onRemove={() => setFile(null)}
        >
          <Button icon={<UploadOutlined />}>Choose a file</Button>
        </Upload>
      </Modal>

      <Modal
        visible={action === 'decline'}
        title="The insurer declined this"
        okText="Record the decline"
        confirmLoading={saving}
        onCancel={() => setAction(null)}
        onOk={() => runAction('decline', form)}
      >
        <label>Why</label>
        <Input.TextArea
          rows={2}
          value={form.reason}
          onChange={e => setForm({ ...form, reason: e.target.value })}
        />
        <label style={{ display: 'block', marginTop: 12 }}>What happens to the money</label>
        <p style={{ opacity: 0.65 }}>
          The customer paid one figure for cover they did not get. Say now what happens to it —
          this is how a refund stops being something everyone assumes somebody else did.
        </p>
        <Radio.Group
          value={form.disposition}
          onChange={e => setForm({ ...form, disposition: e.target.value })}
        >
          <Space direction="vertical">
            {DECLINE_DISPOSITIONS.map(option => (
              <Radio key={option.value} value={option.value}>
                {option.label}
              </Radio>
            ))}
          </Space>
        </Radio.Group>
      </Modal>
    </PageLoader>
  )
}

const Detail = ({ label, value }) => (
  <Col xs={24} md={12}>
    <div style={{ opacity: 0.6, fontSize: 12 }}>{label}</div>
    <div>{value || value === 0 ? value : '—'}</div>
  </Col>
)

Detail.propTypes = {
  label: PropTypes.string,
  value: PropTypes.any
}

const PoliciesRoutes = props => (
  <RouterSwitch>
    <PrivateRoute exact path={props.match.path} component={PoliciesList} />
  </RouterSwitch>
)

PoliciesRoutes.propTypes = { match: PropTypes.object }

export default withRouter(PoliciesRoutes)
